import { clsx } from 'clsx';

import { Typography } from '@/ui';

import type { FC, ReactNode } from 'react';

/**
 * The time column's heading text for a `YYYY-MM-DD` day, e.g. "Thu, Sep 10",
 * with the member scheduler's empty-state copy when no day is chosen.
 */
export const mentoCalBookingDayHeading = (isoDate: string): string =>
  isoDate
    ? new Date(`${isoDate}T00:00:00`).toLocaleDateString('en', {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
    : 'Select a date';

interface Props {
  /** The slot buttons. Each flow maps its own slot shape and click handler. */
  children: ReactNode;
  /** Extra classes for the column wrapper, e.g. `flex-1` to stretch. */
  className?: string;
  /** The day the listed times belong to, e.g. "Thu, Sep 10". */
  heading: string;
  /**
   * How the scroller is bounded. Deliberately per-flow: the member scheduler
   * sits inside a fixed-height card and bounds with `flex-1`, while the admin
   * page's card grows with its content and needs an explicit cap. Sharing one
   * of those would break the other's layout.
   */
  scrollClassName: string;
}

/**
 * The time column's heading and scroll region.
 *
 * MEN-3990: the Figma heads the column with its day and clips the list rather
 * than letting it grow (file `t3BrPD8G21GljDbYsf4AXN`, node `16264:33105`).
 * Both booking flows need exactly that, so the chrome lives here and only the
 * height constraint is passed in.
 */
export const MentoCalTimeList: FC<Props> = ({
  children,
  className,
  heading,
  scrollClassName,
}) => (
  <div className={clsx('flex min-h-0 flex-col gap-4', className)}>
    <Typography className="text-base font-bold leading-[26px]">
      {heading}
    </Typography>
    <div
      aria-label="Available times"
      className={clsx(
        'flex min-h-0 flex-col gap-2 overflow-y-auto',
        scrollClassName,
      )}
      role="region"
    >
      {children}
    </div>
  </div>
);
